import { useEffect, useMemo, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import {
  FiSave,
  FiRotateCcw,
  FiTrash2,
  FiAlertTriangle,
  FiAlertCircle,
} from "react-icons/fi";
import { apiRequest } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

export default function CreateSupplierReturnPage() {
  const { accessToken } = useAuth();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();

  const [grns, setGrns] = useState([]);
  const [grnId, setGrnId] = useState(searchParams.get("goodsReceiptId") || "");
  const [grn, setGrn] = useState(null);
  const [items, setItems] = useState([]);
  const [reason, setReason] = useState("Damaged stock");
  const [notes, setNotes] = useState("");
  const [error, setError] = useState("");
  const [isSaving, setIsSaving] = useState(false);

  const loadGrn = async (id) => {
    if (!id) return;

    const response = await apiRequest(`/purchases/grns/${id}`, {
      accessToken,
    });

    setGrn(response);
    setItems(
      response.items
        .filter((item) => Number(item.quantityAccepted) > 0)
        .map((item) => {
          const lot = item.inventoryLot;
          const unreservedLotQuantity = lot
            ? Math.max(
                0,
                Number(lot.remainingQuantity || 0) -
                  Number(lot.reservedQuantity || 0),
              )
            : 0;

          return {
            supplierReceiptItemId: item.id,
            variantId: item.variantId,
            product: item.variant?.product?.name || "Product",
            sku: item.variant?.sku || "—",
            receivedQty: Number(item.quantityAccepted),
            lotNumber: lot?.lotNumber || null,
            lotStatus: lot?.status || null,
            availableQty: Math.min(
              Number(item.quantityAccepted),
              unreservedLotQuantity,
            ),
            returnQty: 0,
            // Cost is shown for transparency; the backend always uses the
            // actual lot cost when it creates the return.
            unitCost: Number(lot?.unitCost ?? item.unitCost ?? 0),
            reason: "Damaged",
          };
        }),
    );
  };

  useEffect(() => {
    async function loadData() {
      try {
        const response = await apiRequest("/purchases/grns?status=POSTED", {
          accessToken,
        });

        setGrns(Array.isArray(response) ? response : []);

        if (grnId) {
          await loadGrn(grnId);
        }
      } catch (requestError) {
        setError(requestError.message || "Could not load posted GRNs.");
      }
    }

    if (accessToken) loadData();
  }, [accessToken]);

  const updateItem = (index, field, value) => {
    setItems((current) =>
      current.map((item, itemIndex) =>
        itemIndex === index
          ? {
              ...item,
              [field]:
                field === "returnQty"
                  ? Math.min(
                      Math.max(0, Number(value || 0)),
                      Number(item.availableQty || 0),
                    )
                  : value,
            }
          : item,
      ),
    );
  };

  const totalValue = useMemo(
    () =>
      items.reduce(
        (sum, item) =>
          sum + Number(item.returnQty || 0) * Number(item.unitCost || 0),
        0,
      ),
    [items],
  );

  const totalQuantity = useMemo(
    () => items.reduce((sum, item) => sum + Number(item.returnQty || 0), 0),
    [items],
  );

  const createReturn = async (shouldDispatch) => {
    setError("");

    const selectedItems = items.filter(
      (item) => Number(item.returnQty) > 0,
    );

    if (!grnId || !grn) {
      setError("Please select a posted GRN.");
      return;
    }

    if (!reason.trim() || selectedItems.length === 0) {
      setError("Add a return reason and at least one return quantity.");
      return;
    }

    if (
      selectedItems.some(
        (item) =>
          !item.lotNumber ||
          Number(item.returnQty) > Number(item.availableQty),
      )
    ) {
      setError(
        "Every return must have a tracked lot and cannot exceed its unreserved available quantity.",
      );
      return;
    }

    try {
      setIsSaving(true);

      const supplierReturn = await apiRequest("/purchases/returns", {
        method: "POST",
        accessToken,
        body: {
          goodsReceiptId: grnId,
          reason,
          notes: notes || undefined,
          items: selectedItems.map((item) => ({
            supplierReceiptItemId: item.supplierReceiptItemId,
            variantId: item.variantId,
            quantityReturned: Number(item.returnQty),
            reason: item.reason,
          })),
        },
      });

      if (shouldDispatch) {
        await apiRequest(
          `/purchases/returns/${supplierReturn.id}/dispatch`,
          {
            method: "POST",
            accessToken,
          },
        );
      }

      navigate("/admindashboard/purchases/returns");
    } catch (requestError) {
      setError(requestError.message || "Could not create supplier return.");
    } finally {
      setIsSaving(false);
    }
  };

  return (
    <div>
      <div className="mb-8">
        <p className="text-sm text-gray-500">Purchasing / Supplier Returns</p>
        <h1 className="mt-1 text-3xl font-bold">Create Supplier Return</h1>
        <p className="mt-2 text-gray-500">
          Return damaged or incorrect inventory to suppliers.
        </p>
      </div>

      {error && (
        <div className="mb-6 flex gap-3 rounded-2xl border border-red-100 bg-red-50 p-5 text-sm text-red-700">
          <FiAlertCircle className="mt-0.5 shrink-0" />
          <p>{error}</p>
        </div>
      )}

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">Return Information</h2>

        <div className="grid gap-4 lg:grid-cols-4">
          <input
            value="Generated automatically"
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
          />

          <select
            value={grnId}
            onChange={async (event) => {
              setGrnId(event.target.value);
              await loadGrn(event.target.value);
            }}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="">Select Posted GRN</option>
            {grns.map((item) => (
              <option key={item.id} value={item.id}>
                {item.grnNumber} — {item.supplier?.displayName}
              </option>
            ))}
          </select>

          <input
            value="DRAFT"
            readOnly
            className="h-12 rounded-xl border border-gray-200 bg-gray-50 px-4 text-gray-500"
          />

          <select
            value={reason}
            onChange={(event) => setReason(event.target.value)}
            className="h-12 rounded-xl border border-gray-200 px-4"
          >
            <option value="Damaged stock">Damaged stock</option>
            <option value="Defective product">Defective product</option>
            <option value="Wrong item received">Wrong item received</option>
            <option value="Supplier quality issue">Supplier quality issue</option>
          </select>
        </div>
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-5 text-lg font-semibold">Supplier Information</h2>
        <div className="grid gap-5 lg:grid-cols-4">
          <Info label="Supplier" value={grn?.supplier?.displayName} />
          <Info label="GRN Number" value={grn?.grnNumber} />
          <Info label="Invoice Number" value={grn?.supplierInvoiceNumber} />
          <Info label="Return Warehouse" value={grn?.store?.name} />
        </div>
      </div>

      <div className="mb-6 overflow-hidden rounded-3xl border border-gray-100 bg-white">
        <div className="border-b border-gray-100 p-6">
          <h2 className="text-lg font-semibold">Return Items</h2>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full min-w-[1100px]">
            <thead>
              <tr className="bg-gray-50">
                <th className="px-6 py-4 text-left">Product</th>
                <th className="px-6 py-4 text-left">Source Lot</th>
                <th className="px-6 py-4 text-center">Accepted Qty</th>
                <th className="px-6 py-4 text-center">Available to Return</th>
                <th className="px-6 py-4 text-center">Return Qty</th>
                <th className="px-6 py-4 text-right">Lot Cost</th>
                <th className="px-6 py-4 text-left">Reason</th>
                <th className="px-6 py-4 text-right">Value</th>
                <th className="px-6 py-4" />
              </tr>
            </thead>

            <tbody>
              {items.map((item, index) => (
                <tr key={item.supplierReceiptItemId} className="border-t border-gray-100">
                  <td className="px-6 py-4">
                    <p className="font-medium">{item.product}</p>
                    <p className="text-xs text-gray-500">{item.sku}</p>
                  </td>
                  <td className="px-6 py-4">
                    {item.lotNumber ? (
                      <>
                        <p className="font-mono text-sm font-semibold text-primary-800">
                          {item.lotNumber}
                        </p>
                        <p className="mt-1 text-xs text-gray-500">
                          {item.lotStatus || "ACTIVE"}
                        </p>
                      </>
                    ) : (
                      <span className="text-xs font-medium text-red-600">
                        Legacy GRN — no lot
                      </span>
                    )}
                  </td>
                  <td className="px-6 py-4 text-center">{item.receivedQty}</td>
                  <td className="px-6 py-4 text-center font-semibold text-emerald-700">
                    {item.availableQty}
                  </td>
                  <td className="px-6 py-4">
                    <input
                      type="number"
                      min="0"
                      max={item.availableQty}
                      step="0.001"
                      value={item.returnQty}
                      onChange={(event) =>
                        updateItem(index, "returnQty", event.target.value)
                      }
                      disabled={!item.lotNumber || item.availableQty <= 0}
                      className="h-11 w-full rounded-xl border border-gray-200 px-3"
                    />
                  </td>
                  <td className="px-6 py-4 text-right text-sm font-medium text-gray-700">
                    ₹{Number(item.unitCost || 0).toLocaleString("en-IN", {
                      minimumFractionDigits: 2,
                      maximumFractionDigits: 2,
                    })}
                  </td>
                  <td className="px-6 py-4">
                    <select
                      value={item.reason}
                      onChange={(event) =>
                        updateItem(index, "reason", event.target.value)
                      }
                      className="h-11 w-full rounded-xl border border-gray-200 px-3"
                    >
                      <option>Damaged</option>
                      <option>Defective</option>
                      <option>Wrong Item</option>
                      <option>Quality Issue</option>
                    </select>
                  </td>
                  <td className="px-6 py-4 text-right font-semibold text-red-600">
                    ₹{(Number(item.returnQty || 0) * Number(item.unitCost || 0)).toLocaleString("en-IN")}
                  </td>
                  <td className="px-6 py-4 text-right">
                    <button
                      type="button"
                      onClick={() =>
                        updateItem(index, "returnQty", 0)
                      }
                      className="text-red-500"
                    >
                      <FiTrash2 />
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>

      <div className="mb-6 rounded-3xl border border-gray-100 bg-white p-6">
        <h2 className="mb-4 font-semibold">Return Notes</h2>
        <textarea
          rows="5"
          value={notes}
          onChange={(event) => setNotes(event.target.value)}
          placeholder="Reason for supplier return..."
          className="w-full rounded-xl border border-gray-200 p-4"
        />
      </div>

      <div className="mb-6 grid gap-6 lg:grid-cols-3">
        <Summary title="Return Value" value={`₹${totalValue.toLocaleString("en-IN")}`} icon={<FiAlertTriangle className="text-red-600" size={20} />} />
        <Summary title="Inventory Impact" value={`-${totalQuantity}`} text="Units will be removed only when dispatched." />
        <Summary title="Debit Note" text="Will be handled in the future finance module." />
      </div>

      <div className="flex justify-end gap-3">
        <button
          type="button"
          disabled={isSaving}
          onClick={() => createReturn(false)}
          className="flex h-11 items-center gap-2 rounded-xl border border-gray-200 px-6 disabled:opacity-60"
        >
          <FiSave />
          Save Draft
        </button>

        <button
          type="button"
          disabled={isSaving}
          onClick={() => createReturn(true)}
          className="flex h-11 items-center gap-2 rounded-xl bg-primary-700 px-6 text-white disabled:opacity-60"
        >
          <FiRotateCcw />
          {isSaving ? "Processing..." : "Dispatch Return"}
        </button>
      </div>
    </div>
  );
}

function Info({ label, value }) {
  return (
    <div>
      <p className="mb-1 text-xs text-gray-500">{label}</p>
      <p className="font-medium">{value || "—"}</p>
    </div>
  );
}

function Summary({ title, value, text, icon }) {
  return (
    <div className="rounded-3xl border border-gray-100 bg-white p-6">
      <div className="mb-4 flex items-center gap-3">
        {icon}
        <h3 className="font-semibold">{title}</h3>
      </div>
      {value && <h2 className="text-3xl font-bold">{value}</h2>}
      {text && <p className="mt-2 text-sm text-gray-500">{text}</p>}
    </div>
  );
}
